import { createContext, useContext, useEffect, useState } from "react";
import { AuthContext } from "./AuthContext";
import { SIDEBAR_ICON_MAP, SIDEBAR_ROUTE_MAP } from "../configs/sidebarConfig";
import { permissionsService } from "../services/api/permissionsService";
import { customerProfileService } from "../services/api/customerProfileService";

export const SidebarLayoutContext = createContext();

export const SidebarLayoutProvider = ({ children }) => {
  const { token } = useContext(AuthContext);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [navLinks, setNavLinks] = useState([]);
  const [pendingApprovalCount, setPendingApprovalCount] = useState(0);
  const [permissions, setPermissions] = useState([]);

  const toggleSidebar = () => setIsSidebarOpen((p) => !p);

  const hasPermission = (nameSlug) => {
    if (!permissions || !permissions.length) return false;
    return permissions.some((group) =>
      group.permissions?.some((p) => p.nameSlug === nameSlug)
    );
  };

  const fetchPendingApproval = async () => {
    if (!token) return;

    try {
      const res =
        await customerProfileService.get_request_profile_pending(token);

      if (res?.success) {
        setPendingApprovalCount(res.pending || 0);
      }
    } catch (err) {
      console.error("Fetch pending approval failed:", err);
    }
  };

  useEffect(() => {
    if (!token) return;

    fetchPendingApproval();
    const interval = setInterval(fetchPendingApproval, 30000);

    return () => clearInterval(interval);
  }, [token]);

  useEffect(() => {
    if (!token) return;

    const fetchPermissions = async () => {
      try {
        const res = await permissionsService.get_me_permission(token);
        setPermissions(res?.groupPermissions || []);

        const menus = res?.groupPermissions
          ?.filter((g) => g.isSidebar)
          ?.map((g) => {
            const subs = g.permissions
              ?.filter((p) => p.isSidebar)
              ?.map((p) => ({
                label: p.name,
                to: SIDEBAR_ROUTE_MAP[p.nameSlug],
              }));

            return {
              label: g.groupName,
              to: SIDEBAR_ROUTE_MAP[g.groupSlug] || "#",
              icon: SIDEBAR_ICON_MAP[g.groupSlug],
              subLinks: subs?.length ? subs : undefined,

              badge:
                g.groupSlug === "quan_ly_xet_duyet"
                  ? pendingApprovalCount
                  : undefined,
            };
          });

        setNavLinks(menus);
      } catch (err) {
        console.error("Fetch permissions failed:", err);
      }
    };

    fetchPermissions();
  }, [token, pendingApprovalCount]);

  const hasSidebar = navLinks && navLinks.length > 0;

  return (
    <SidebarLayoutContext.Provider
      value={{
        isSidebarOpen,
        toggleSidebar,
        navLinks,
        hasSidebar,
        hasPermission,
        permissions,
      }}
    >
      {children}
    </SidebarLayoutContext.Provider>
  );
};

export const useSidebarLayout = () => useContext(SidebarLayoutContext);
